import React, { useCallback, useEffect, useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { createPortal } from "react-dom";
import { MessageCircle, Send, Loader2, Trash2, Flag, Ban, EyeOff } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useRealtime } from "@/context/RealtimeContext";

function QuestionCard({ item, owner, onChanged }) {
  const { t } = useLanguage();
  const [answer, setAnswer] = useState(item.answer || "");
  const [busy, setBusy] = useState(false);

  const run = async (fn, success) => {
    setBusy(true);
    try { await fn(); if (success) toast.success(success); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (owner) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-orange-500 text-white"><MessageCircle className="h-4 w-4" /></div>
          <div className="min-w-0 flex-1">
            <p className="whitespace-pre-wrap break-words text-sm font-semibold leading-6 text-slate-800 dark:text-slate-100">{item.text}</p>
            <p className="mt-1 text-[11px] font-medium text-slate-400">Anonymous · {new Date(item.created_at).toLocaleDateString("ja-JP")}</p>
          </div>
        </div>
        {item.answer ? (
          <div className="mt-4 rounded-2xl bg-gradient-to-br from-pink-50 to-purple-50 p-4 dark:from-pink-500/10 dark:to-purple-500/10">
            <p className="text-[11px] font-bold uppercase tracking-wide text-pink-500">{t("Your answer")}</p>
            <p className="mt-1 whitespace-pre-wrap break-words text-sm leading-6 text-slate-700 dark:text-slate-200">{item.answer}</p>
          </div>
        ) : (
          <div className="mt-4">
            <textarea value={answer} onChange={(e) => setAnswer(e.target.value.slice(0, 1000))} placeholder="Answer publicly..." className="min-h-[90px] w-full resize-none rounded-2xl border border-slate-200 bg-slate-50 p-3 text-sm outline-none focus:border-pink-300 focus:ring-2 focus:ring-pink-100 dark:border-slate-700 dark:bg-slate-950" />
            <div className="mt-2 flex flex-wrap justify-end gap-2">
              <button disabled={busy} onClick={() => run(async () => { await api.post(`/questions/${item.id}/report`); await api.post(`/questions/${item.id}/block`); await api.delete(`/questions/${item.id}`); }, t("Question reported and sender blocked"))} className="rounded-full px-3 py-2 text-xs font-semibold text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800"><Flag className="mr-1 inline h-3 w-3" />{t("Report")}</button>
              <button disabled={busy} onClick={() => run(() => api.post(`/questions/${item.id}/block`), "Sender blocked")} className="rounded-full px-3 py-2 text-xs font-semibold text-slate-400 hover:bg-slate-100 hover:text-red-500"><Ban className="mr-1 inline h-3 w-3" />Block sender</button>
              <button disabled={busy || !answer.trim()} onClick={() => run(() => api.post(`/questions/${item.id}/answer`, { answer: answer.trim() }), "Answer posted")} className="rounded-full bg-orange-500 px-4 py-2 text-xs font-bold text-white hover:bg-orange-600 disabled:opacity-50"><Send className="mr-1 inline h-3 w-3" />Answer publicly</button>
            </div>
          </div>
        )}
        <div className="mt-2 flex justify-end">
          <button disabled={busy} onClick={() => run(() => api.delete(`/questions/${item.id}`), t("Question deleted"))} className="rounded-full px-3 py-1.5 text-xs font-semibold text-slate-400 hover:bg-red-50 hover:text-red-500"><Trash2 className="mr-1 inline h-3 w-3" />{t("Delete")}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-orange-500 text-white"><MessageCircle className="h-4 w-4" /></div>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-bold uppercase tracking-wide text-pink-500">Anonymous question</p>
          <p className="mt-1 whitespace-pre-wrap break-words text-sm font-semibold leading-6 text-slate-800 dark:text-slate-100">{item.text}</p>
        </div>
      </div>
      {item.answer && <div className="mt-3 rounded-2xl bg-slate-50 p-4 dark:bg-slate-950"><p className="text-[11px] font-bold uppercase tracking-wide text-purple-500">Answer</p><p className="mt-1 whitespace-pre-wrap break-words text-sm leading-6 text-slate-700 dark:text-slate-200">{item.answer}</p></div>}
    </div>
  );
}

export default function AnonymousQuestions({ targetUsername, owner = false, enabled: publicEnabled = true, compact = false }) {
  const { t } = useLanguage();
  const [items, setItems] = useState([]);
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [askOpen, setAskOpen] = useState(false);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const { subscribe } = useRealtime();
  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (owner) {
        const { data } = await api.get("/questions/inbox");
        setItems(data?.items || []);
        setEnabled(data?.enabled !== false);
      } else if (targetUsername) {
        const { data } = await api.get(`/users/${encodeURIComponent(targetUsername)}/questions`);
        setItems(Array.isArray(data) ? data : []);
      }
    } catch (e) {
      if (owner) toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  }, [owner, targetUsername]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const handler = (event) => {
      if (event.detail?.type === "anonymous-question:new" || event.detail?.type === "anonymous-question:answered") load();
    };
    window.addEventListener("yaprio:anonymous-question", handler);
    return () => window.removeEventListener("yaprio:anonymous-question", handler);
  }, [load]);

  const send = async () => {
    if (!text.trim() || !targetUsername) return;
    setSending(true);
    try {
      await api.post(`/users/${encodeURIComponent(targetUsername)}/questions`, { text: text.trim() });
      setText(""); setAskOpen(false);
      toast.success(t("Question sent anonymously"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const toggle = async () => {
    try {
      const { data } = await api.put("/questions/settings", { enabled: !enabled });
      setEnabled(data.enabled);
      toast.success(data.enabled ? "Anonymous questions enabled" : "Anonymous questions disabled");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!owner && publicEnabled === false) return null;

  if (!owner && compact) {
    return (
      <div className="mt-1">
        <button type="button" onClick={() => setAskOpen(true)} className="inline-flex h-9 items-center gap-2 rounded-full border border-pink-200/60 bg-white px-4 text-xs font-bold text-pink-600 shadow-sm transition hover:border-pink-300 hover:bg-pink-50">
          <MessageCircle className="h-3.5 w-3.5" />Ask an anonymous question
        </button>
        {askOpen && <AskDialog text={text} setText={setText} sending={sending} onClose={() => setAskOpen(false)} onSend={send} />}
      </div>
    );
  }

  if (!owner && !items.length) {
    return (
      <div className="mt-4">
        {askOpen && <AskDialog text={text} setText={setText} sending={sending} onClose={() => setAskOpen(false)} onSend={send} />}
      </div>
    );
  }

  return (
    <section className="mt-5">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div><h2 className="font-heading text-lg font-extrabold text-slate-900 dark:text-white">{owner ? t("Anonymous Questions") : t("Questions & Answers")}</h2><p className="text-xs text-slate-400">{owner ? t("Questions are anonymous to you, while Yaprio keeps internal abuse-prevention records.") : t("Answered anonymously for everyone to see.")}</p></div>
        {owner ? <button type="button" onClick={toggle} className={`inline-flex items-center gap-2 rounded-full px-3 py-2 text-xs font-bold ${enabled ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"}`}><span className={`h-2 w-2 rounded-full ${enabled ? "bg-emerald-500" : "bg-slate-400"}`} />{enabled ? "Enabled" : "Disabled"}</button> : null}
      </div>
      {loading ? <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-pink-500" /></div> : items.length ? <div className="space-y-3">{items.map((item) => <QuestionCard key={item.id} item={item} owner={owner} onChanged={load} />)}</div> : <div className="rounded-2xl border border-dashed border-slate-200 p-8 text-center text-sm text-slate-400 dark:border-slate-800">{owner ? t("No anonymous questions yet.") : t("No answered questions yet.")}</div>}
      {askOpen && <AskDialog text={text} setText={setText} sending={sending} onClose={() => setAskOpen(false)} onSend={send} />}
    </section>
  );
}

function AskDialog({ text, setText, sending, onClose, onSend }) {
  if (typeof document === "undefined") return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex min-h-screen items-center justify-center bg-black/55 p-4 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-md rounded-3xl bg-white p-5 shadow-2xl dark:bg-slate-900" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-orange-500 text-white">
            <EyeOff className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-heading text-lg font-extrabold text-slate-900 dark:text-white">Ask anonymously</h3>
            <p className="text-xs text-slate-500">Your username won't be shown to the recipient.</p>
          </div>
        </div>
        <textarea autoFocus value={text} onChange={(e) => setText(e.target.value.slice(0, 500))} placeholder="Ask anything..." className="mt-5 min-h-[130px] w-full resize-none rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm outline-none focus:border-pink-300 focus:ring-2 focus:ring-pink-100 dark:border-slate-700 dark:bg-slate-950 dark:text-white" />
        <div className="mt-2 text-right text-[11px] text-slate-400">{text.length}/500</div>
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-full px-4 py-2 text-sm font-semibold text-slate-500 hover:bg-slate-100">Cancel</button>
          <button type="button" disabled={sending || !text.trim()} onClick={onSend} className="rounded-full bg-orange-500 px-5 py-2 text-sm font-bold text-white hover:bg-orange-600 disabled:opacity-50">{sending ? <Loader2 className="h-4 w-4 animate-spin" /> : t("Send anonymously")}</button>
        </div>
      </div>
    </div>,
    document.body
  );
}